import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useStore from "../store/useStore";

/* ── Gradient palette cycling for banners that have images ── */
const GRADIENTS = [
  "from-[#1a6b3c] via-[#2E7B52] to-[#1F5A3A]",
  "from-[#b45309] via-[#d97706] to-[#92400e]",
  "from-[#1e40af] via-[#2563eb] to-[#1d4ed8]",
  "from-[#7c3aed] via-[#8b5cf6] to-[#6d28d9]",
  "from-[#be123c] via-[#e11d48] to-[#9f1239]",
];

/* ── Skeleton ── */
const BannerSkeleton = () => (
  <section className="relative w-full overflow-hidden px-4 py-2 sm:py-2">
    <div className="h-[165px] animate-pulse overflow-hidden rounded-[24px] bg-gray-200 sm:h-[120px] sm:rounded-3xl md:h-[145px] lg:h-[170px]" />
  </section>
);

const BannerCarousel = () => {
  const { banners, banners_loading, fetchBanners } = useStore();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false); // kept for auto-pause on hover
  const [imgErr, setImgErr] = useState({});

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  useEffect(() => {
    fetchBanners();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-advance every 4 s, pause on hover
  useEffect(() => {
    if (paused || banners.length <= 1) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % banners.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [paused, banners.length]);

  const prev = useCallback(
    () => setCurrent((p) => (p - 1 + banners.length) % banners.length),
    [banners.length],
  );
  const next = useCallback(
    () => setCurrent((p) => (p + 1) % banners.length),
    [banners.length],
  );

  const handleBannerClick = (banner) => {
    navigate(banner.url);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (diff > 50) {
      next();
    } else if (diff < -50) {
      prev();
    }
  };

  if (banners_loading && banners.length === 0) return <BannerSkeleton />;
  if (!banners.length) return null;

  return (
    <section className="relative w-full overflow-hidden px-4 py-2 sm:py-2">
      <div
        className="relative h-[165px] overflow-hidden rounded-[24px] shadow-xl sm:h-[120px] sm:rounded-3xl md:h-[145px] lg:h-[170px]"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {banners.map((banner, index) => {
          const gradient = GRADIENTS[index % GRADIENTS.length];
          const hasImage = banner.image && !imgErr[index];

          return (
            <div
              key={banner.id}
              onClick={() => handleBannerClick(banner)}
              className={`absolute inset-0 cursor-pointer transition-all duration-700 ease-in-out ${index === current
                  ? "opacity-100 scale-100 z-10"
                  : "opacity-0 scale-[1.02] z-0"
                }`}
            >
              {/* Background — image if available, else gradient */}
              {hasImage ? (
                <>
                  <img
                    src={banner.image}
                    alt={`Banner ${index + 1}`}
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={() =>
                      setImgErr((prev) => ({ ...prev, [index]: true }))
                    }
                  />
                  {/* Dark overlay for text readability */}
                  <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
                </>
              ) : (
                <>
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${gradient}`}
                  />
                  {/* Decorative radial blobs */}
                  <div
                    className="absolute inset-0 opacity-30"
                    style={{
                      background:
                        "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.15) 0%, transparent 40%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.1) 0%, transparent 40%)",
                    }}
                  />
                  {/* Diagonal grid */}
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(45deg, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 2px, transparent 2px, transparent 10px)",
                    }}
                  />
                </>
              )}

              {/* Bottom shine strip */}
              {/* <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-white/10 via-white/40 to-white/10" /> */}
            </div>
          );
        })}

        {/* ── Left arrow ── */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            prev();
          }}
          className="absolute left-3 top-1/2 z-20 hidden sm:flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/30 text-white backdrop-blur-sm transition hover:bg-black/50 sm:h-10 sm:w-10"
          aria-label="Previous"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        {/* ── Right arrow ── */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            next();
          }}
          className="absolute right-3 top-1/2 z-20 hidden sm:flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/30 text-white backdrop-blur-sm transition hover:bg-black/50 sm:h-10 sm:w-10"
          aria-label="Next"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>

        {/* ── Dot indicators ── */}
        <div className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
          {banners.map((_, i) => (
            <button
              key={i}
              onClick={(e) => {
                e.stopPropagation();
                setCurrent(i);
              }}
              className={`rounded-full transition-all duration-300 ${i === current
                  ? "w-7 h-2 bg-white"
                  : "w-2 h-2 bg-white/50 hover:bg-white/70"
                }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>

        {/* Counter and Pause indicator removed */}
      </div>
    </section>
  );
};

export default BannerCarousel;
